/**
 * The one date range every report tab is measured over.
 *
 * Lifted to the section rather than repeated per tab: switching from Sales to
 * Expenses must not silently change the window, or two tabs open side by side
 * would be answering different questions with the same-looking numbers.
 *
 * The four presets are what an operator actually asks for; the pickers are there
 * for the fifth thing. Each end bounds the other, so the calendar cannot offer a
 * range that reads backwards.
 *
 * ── WHY THE ROW IS FOLDED ──────────────────────────────────────────────────
 *
 * Six controls drawn at once cost one row on a laptop and THREE on a phone, and
 * they sat above every report - so on a 375px screen the reader scrolled past a
 * block of buttons before reaching a single figure. This page's whole job is to
 * be read, and on most visits every one of those controls is at its default.
 *
 * So the window folds behind one toggle, and the same two rules the register's
 * filter row lives by keep the fold honest (see `ProjectsToolbar`):
 *
 *   - The toggle SAYS WHICH WINDOW IS IN FORCE. That register's toggle carries a
 *     count of what is still on; a date range is never off, so the equivalent
 *     here is the window itself - "12 kuud", or both ends when somebody typed
 *     them. Figures whose period nothing on screen admits to would be the same
 *     failure the count exists to prevent, moved one fold deeper.
 *   - It OPENS BY ITSELF over a window that is not one of the presets. A typed
 *     range is one somebody is still working on, and the fields that made it
 *     should be where they left them.
 *
 * The export menu stays OUT of the fold. It is an action rather than a filter,
 * and it is what a reader reaches for after reading - hiding it behind a control
 * labelled with a date range would file it under the wrong word.
 *
 * The arithmetic lives in `range.ts`, React free, so the labelling rule can be
 * tested without rendering anything (AGENTS.md §4). This file re-exports it, so
 * no call site had to change.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { CalendarRange, ChevronDown } from "lucide-react";
import { cn, DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { Btn, Toolbar, ToolbarGap } from "../../ui";
import { matchedPreset, PRESETS, presetRange, rangeLabel, type PresetKey, type Range } from "./range";

export { PRESETS, presetRange };
export type { PresetKey, Range };

const DATE_PILL = "crm-fld crm-fld-date w-44";

/** The calendar's bound wants a `Date` at midnight local - at any other hour the
 *  picker's `<` test rules out the boundary day itself. */
function bound(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

export function RangeBar({
  range,
  onChange,
  actions,
}: {
  range: Range;
  onChange: (r: Range) => void;
  /**
   * Anything that belongs beside the window rather than under it - today, the
   * export menu.
   *
   * A slot rather than the menu itself, because this file must not learn what a
   * report kind is: it owns one date range and four presets, and every tab uses
   * it. The section knows which tab is open and hands the right control down.
   */
  actions?: ReactNode;
}) {
  const t = useT();
  const { locale } = useI18n();
  const custom = matchedPreset(range) === null;
  const [open, setOpen] = useState(custom);

  /**
   * Open when the window BECOMES a typed one, and never shut it from here.
   *
   * The effect only ever opens. An operator who picked a preset and then folded
   * the row has said what they want, and a rule that re-shut it on the next
   * change would be the page arguing with them. The ref makes this fire on the
   * transition into a typed range rather than on every render inside one, so
   * the fold stays theirs to close.
   */
  const wasCustom = useRef(custom);
  useEffect(() => {
    if (custom && !wasCustom.current) setOpen(true);
    wasCustom.current = custom;
  }, [custom]);

  return (
    <>
      <Toolbar className={cn(open && "mb-2.5")}>
        {/* `is-on` while it is open as well as while the window is a typed one:
            the pill has to look pressed when it IS pressed, or the row below
            looks like it opened by itself.

            `flex-initial` (0 1 auto), because `.crm-fld` is `flex: 1 1 200px` -
            written for a search box, which SHOULD eat the row it is on. A
            toggle that grew the same way filled 331px of a 355px phone with the
            words "12 kuud" and pushed the export menu onto a second row, which
            is most of the space this fold was meant to give back. It may still
            SHRINK, so a typed window's two dates ellipse rather than overflow. */}
        <button
          type="button"
          className={cn(
            "crm-fld crm-fld-sel flex-initial w-auto min-w-0 gap-2.5 px-4 text-[12.75px]",
            (open || custom) && "is-on",
          )}
          aria-expanded={open}
          aria-controls="reports-range-row"
          onClick={() => setOpen((v) => !v)}
        >
          <CalendarRange className="size-4" />
          <span className="min-w-0 truncate">
            {rangeLabel(range, locale, (key) => t(`reports.preset.${key}`))}
          </span>
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>

        <ToolbarGap />
        {actions}
      </Toolbar>

      {/* Unmounted rather than hidden, so two calendars are not sitting in the
          page each holding a month nobody has asked to see. */}
      {open ? (
        <Toolbar id="reports-range-row">
          {PRESETS.map((p) => {
            const preset = presetRange(p);
            const on = preset.from === range.from && preset.to === range.to;
            return (
              <Btn key={p} small tone={on ? "dark" : "plain"} onClick={() => onChange(preset)}>
                {t(`reports.preset.${p}`)}
              </Btn>
            );
          })}
          <ToolbarGap />
          <DatePicker
            value={range.from}
            onChange={(from) => onChange({ ...range, from })}
            toDate={bound(range.to)}
            locale={locale}
            placeholder={t("reports.from")}
            ariaLabel={t("reports.from")}
            className={DATE_PILL}
          />
          <DatePicker
            value={range.to}
            onChange={(to) => onChange({ ...range, to })}
            fromDate={bound(range.from)}
            locale={locale}
            placeholder={t("reports.to")}
            ariaLabel={t("reports.to")}
            className={DATE_PILL}
          />
        </Toolbar>
      ) : null}
    </>
  );
}
